import type { ContentAST } from '@shared/ast/astTypes'
import { findAnyById, getBlockText, getInlineText } from '@shared/ast/astUtils'
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootNode,
  type LexicalNode,
  type PointType,
} from 'lexical'

export interface EmbedSelectionSnapshot {
  ast: ContentAST
  startBlockId: string
  startOffset: number
  endBlockId: string
  endOffset: number
  collapsed: boolean
}

/** Resolve Lexical positions, counting an inline embed as one opaque character.
 * DOM range text includes fetched prose and therefore cannot provide these offsets.
 */
export function $captureEmbedSelection(ast: ContentAST): EmbedSelectionSnapshot | null {
  const selection = $getSelection()
  if (!$isRangeSelection(selection)) {
    return null
  }
  const point = (position: PointType, end: boolean): { id: string; offset: number } | null => {
    let node: LexicalNode = position.getNode()
    let offset = position.offset
    if (position.type === 'element' && $isElementNode(node)) {
      // Root/list element offsets count children. Descend to the actual selected
      // edge so a span ending in a nested list uses its last item's own offset.
      const adjacent = node.getChildAtIndex(end ? offset - 1 : offset)
      if (adjacent) {
        node = adjacent
        while ($isElementNode(node) && node.getChildrenSize() > 0) {
          node = (end ? node.getLastChild() : node.getFirstChild())!
        }
        offset = end ? node.getTextContentSize() : 0
      } else {
        offset = end ? 0 : node.getTextContentSize()
      }
    }
    let current: LexicalNode | null = node
    while (current && !$isRootNode(current)) {
      const identified = current as LexicalNode & { getBlockId?: () => string }
      const id = identified.getBlockId?.()
      if (id) {
        const target = findAnyById(ast, id)
        if (!target) {
          return null
        }
        const length = ('type' in target ? getBlockText(target) : getInlineText(target.content)).length
        return { id, offset: Math.min(length, Math.max(0, offset)) }
      }
      offset += current.getPreviousSiblings().reduce((sum, sibling) => sum + sibling.getTextContentSize(), 0)
      current = current.getParent()
    }
    return null
  }
  const backward = selection.isBackward()
  const start = point(backward ? selection.focus : selection.anchor, false)
  const end = selection.isCollapsed() ? start : point(backward ? selection.anchor : selection.focus, true)
  if (!start || !end) {
    return null
  }
  return {
    ast,
    startBlockId: start.id,
    startOffset: start.offset,
    endBlockId: end.id,
    endOffset: end.offset,
    collapsed: selection.isCollapsed(),
  }
}
